import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Trophy,
  Search,
  ChevronRight,
  Crown,
  Loader2,
  UserPlus,
  MapPin
} from 'lucide-react';
import { generateInitials } from '../lib/utils';

// Tunisian Governorates/Districts
const TUNISIAN_DISTRICTS = [
  "Tunis", "Ariana", "Ben Arous", "Manouba", "Nabeul", "Zaghouan", "Bizerte",
  "Béja", "Jendouba", "Kef", "Siliana", "Sousse", "Monastir", "Mahdia",
  "Sfax", "Kairouan", "Kasserine", "Sidi Bouzid", "Gabès", "Médenine",
  "Tataouine", "Gafsa", "Tozeur", "Kébili"
];

export default function Teams() {
  const { user, refreshUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '', max_members: 11, district: '' });

  const fetchTeams = async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        api.get('/teams'),
        api.get('/teams/my'),
      ]);
      setTeams(allRes.data || []);
      setMyTeams(myRes.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeam.name.trim()) {
      toast.error('Teamname erforderlich');
      return;
    }
    
    if (!newTeam.district) {
      toast.error('Bitte wähle einen Bezirk aus');
      return;
    }

    setCreating(true);
    try {
      await api.post(`/teams?name=${encodeURIComponent(newTeam.name)}&description=${encodeURIComponent(newTeam.description)}&max_members=${newTeam.max_members}&district=${encodeURIComponent(newTeam.district)}`);
      toast.success('Team erstellt!');
      setCreateOpen(false);
      setNewTeam({ name: '', description: '', max_members: 11, district: '' });
      await fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinTeam = async (teamId) => {
    try {
      await api.post(`/teams/${teamId}/join`);
      toast.success('Bewerbung gesendet!');
      await fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei Bewerbung');
    }
  };

  // Filter teams by search query and district
  const filteredTeams = teams.filter(team => {
    const matchesSearch = team.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDistrict = 
      districtFilter === 'all' || 
      (districtFilter === 'none' && !team.district) ||
      team.district === districtFilter;
    return matchesSearch && matchesDistrict;
  });

  // Get unique districts from available teams for the filter
  const availableDistricts = [...new Set(teams.map(t => t.district).filter(Boolean))].sort();
  
  // Count teams without district
  const teamsWithoutDistrict = teams.filter(t => !t.district).length;

  const TeamCard = ({ team, isMember = false }) => {
    const isCaptain = team.captain_id === user?.id;
    const maxMembers = team.max_members || 11;
    const isFull = team.member_count >= maxMembers;
    
    return (
      <Card className="hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-slate-200">
        <CardContent className="p-3 sm:p-5">
          <div className="flex items-start gap-3 sm:gap-4">
            <Avatar className="h-12 w-12 sm:h-16 sm:w-16 border-2 border-tunis-blue/20">
              <AvatarFallback className="bg-gradient-to-br from-tunis-blue to-tunis-teal text-white text-base sm:text-xl font-bold">
                {generateInitials(team.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 flex-wrap">
                <h3 className="font-display font-bold text-base sm:text-lg text-tunis-blue truncate">
                  {team.name}
                </h3>
                {isCaptain && (
                  <Badge className="bg-amber-500/10 text-amber-600 text-[10px] sm:text-xs">
                    <Crown className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                    Kapitän
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs sm:text-sm mb-1">
                <MapPin className="w-3 h-3" />
                {team.district ? (
                  <span className="text-tunis-teal">{team.district}</span>
                ) : (
                  <span className="text-amber-500 italic">Kein Bezirk</span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-2 sm:mb-3">
                {team.description || 'Kein Beschreibung'}
              </p>
              <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 text-tunis-blue" />
                  <span className={isFull ? 'text-amber-600 font-medium' : ''}>
                    {team.member_count}/{maxMembers}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500" />
                  <span>{team.team_elo} ELO</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <div className="text-xs sm:text-sm text-muted-foreground">
              {team.wins}S / {team.losses}N / {team.draws || 0}U
            </div>
            {isMember ? (
              <Link to={`/teams/${team.id}`} className="w-full sm:w-auto">
                <Button size="sm" className="btn-secondary w-full sm:w-auto text-xs sm:text-sm">
                  Details
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Button>
              </Link>
            ) : isFull ? (
              <Badge className="bg-amber-500/10 text-amber-600 text-xs">Team voll</Badge>
            ) : (
              <Button 
                size="sm" 
                className="btn-primary w-full sm:w-auto text-xs sm:text-sm"
                onClick={() => handleJoinTeam(team.id)}
              >
                <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Bewerben
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <Skeleton className="h-6 sm:h-8 w-32 sm:w-48 mb-4 sm:mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-40 sm:h-48 rounded-xl sm:rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24" data-testid="teams-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div>
            <h1 className="font-display font-bold text-xl sm:text-2xl md:text-3xl text-tunis-blue">
              Teams
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Erstelle oder trete einem Team bei</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary w-full sm:w-auto" data-testid="create-team-btn">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Team erstellen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Neues Team erstellen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTeam} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Teamname *</Label>
                  <Input
                    value={newTeam.name}
                    onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. FC Tunis Stars"
                    data-testid="input-team-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-tunis-teal" />
                    Bezirk / Gouvernorat *
                  </Label>
                  <Select
                    value={newTeam.district}
                    onValueChange={(value) => setNewTeam(prev => ({ ...prev, district: value }))}
                  >
                    <SelectTrigger data-testid="select-district">
                      <SelectValue placeholder="Bezirk auswählen..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {TUNISIAN_DISTRICTS.map((district) => (
                        <SelectItem key={district} value={district}>
                          {district}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Beschreibung</Label>
                  <Textarea
                    value={newTeam.description}
                    onChange={(e) => setNewTeam(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Erzähle etwas über dein Team..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Maximale Teamgröße</Label>
                  <Select
                    value={newTeam.max_members.toString()}
                    onValueChange={(value) => setNewTeam(prev => ({ ...prev, max_members: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 Spieler (Futsal)</SelectItem>
                      <SelectItem value="7">7 Spieler (Kleinfeld)</SelectItem>
                      <SelectItem value="11">11 Spieler (Standard)</SelectItem>
                      <SelectItem value="15">15 Spieler (mit Ersatz)</SelectItem>
                      <SelectItem value="20">20 Spieler (großes Team)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={creating}>
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Team erstellen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
            <Input
              placeholder="Team suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 sm:pl-10 h-10 sm:h-12 text-sm sm:text-base"
              data-testid="team-search"
            />
          </div>
          <div className="w-full sm:w-64">
            <Select value={districtFilter} onValueChange={setDistrictFilter}>
              <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-tunis-teal" />
                  <SelectValue placeholder="Alle Bezirke" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Bezirke</SelectItem>
                {teamsWithoutDistrict > 0 && (
                  <SelectItem value="none" className="text-amber-600">
                    Ohne Bezirk ({teamsWithoutDistrict})
                  </SelectItem>
                )}
                {TUNISIAN_DISTRICTS.map((district) => {
                  const count = teams.filter(t => t.district === district).length;
                  return (
                    <SelectItem key={district} value={district}>
                      {district} {count > 0 && `(${count})`}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filter Badge */}
        {districtFilter !== 'all' && (
          <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
            <Badge className={`px-2 sm:px-3 py-1 text-xs sm:text-sm ${districtFilter === 'none' ? 'bg-amber-500/10 text-amber-600' : 'bg-tunis-teal/10 text-tunis-teal'}`}>
              <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
              {districtFilter === 'none' ? 'Ohne Bezirk' : districtFilter}
              <button
                onClick={() => setDistrictFilter('all')}
                className="ml-2 hover:text-tunis-blue"
              >
                ×
              </button>
            </Badge>
            <span className="text-xs sm:text-sm text-muted-foreground">
              {filteredTeams.length} Team{filteredTeams.length !== 1 ? 's' : ''} gefunden
            </span>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="all">
          <TabsList className="mb-4 sm:mb-6 w-full sm:w-auto">
            <TabsTrigger value="all" data-testid="tab-all-teams" className="flex-1 sm:flex-none text-xs sm:text-sm">
              Alle Teams ({filteredTeams.length})
            </TabsTrigger>
            <TabsTrigger value="my" data-testid="tab-my-teams" className="flex-1 sm:flex-none text-xs sm:text-sm">
              Meine Teams ({myTeams.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {filteredTeams.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {filteredTeams.map((team) => (
                  <TeamCard 
                    key={team.id} 
                    team={team} 
                    isMember={myTeams.some(t => t.id === team.id)}
                  />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-8 sm:py-12 text-center">
                  <Users className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg text-tunis-blue mb-2">
                    Keine Teams gefunden
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4">
                    Erstelle das erste Team!
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="my">
            {myTeams.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myTeams.map((team) => (
                  <TeamCard key={team.id} team={team} isMember={true} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg text-tunis-blue mb-2">
                    Noch kein Team
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Erstelle ein Team oder trete einem bei.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
